import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, CalendarDays, Tag } from "lucide-react";
import BeforeAfter from "../components/BeforeAfter";
import CtaSection from "../components/CtaSection";
import SectionHeading from "../components/SectionHeading";
import { MaskLine, Reveal } from "../components/Reveal";
import { api } from "../lib/api";
import { IMAGES } from "../data/site";

export default function WorkDetail() {
  const { id } = useParams();
  const [work, setWork] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    api
      .get(`/works/${id}`)
      .then((r) => {
        setWork(r.data);
        setState("ok");
      })
      .catch(() => setState("error"));
  }, [id]);

  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="font-mono text-sm tracking-[0.3em] text-gray-500">LOADING...</p>
      </div>
    );
  }

  if (state === "error" || !work) {
    return (
      <div data-testid="work-not-found" className="flex min-h-screen flex-col items-center justify-center gap-6">
        <p className="font-mincho text-2xl font-bold text-white">実績が見つかりません</p>
        <Link to="/works" className="border border-line px-6 py-3 font-display text-xs font-bold tracking-[0.2em] text-gray-300 transition-colors hover:border-accent hover:text-accent">
          実績一覧へ戻る
        </Link>
      </div>
    );
  }

  const hasBeforeAfter = Boolean(work.before_image_url && work.after_image_url);

  return (
    <div data-testid="work-detail-page">
      <section className="relative overflow-hidden pb-16 pt-36 md:pb-20 md:pt-48">
        <img src={work.image_url || IMAGES.civil} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/80 to-ink" />
        <div className="absolute inset-0 bg-blueprint opacity-30" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Link to="/works" data-testid="work-back-link" className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-gray-400 transition-colors hover:text-accent">
              <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
              WORKS — 実績一覧
            </Link>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="mt-10 flex items-center gap-3 font-mono text-xs tracking-[0.35em] text-accent">
              <span className="h-px w-10 bg-accent" />
              {work.category} — {work.year}
            </p>
          </Reveal>
          <h1 className="mt-6">
            <MaskLine className="font-mincho text-3xl font-bold leading-tight text-white sm:text-5xl">{work.title}</MaskLine>
          </h1>
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap gap-3">
              <span className="flex items-center gap-2 border border-line bg-panel px-4 py-2 text-xs text-gray-300">
                <MapPin className="h-3.5 w-3.5 text-accent" />
                {work.location || "—"}
              </span>
              <span className="flex items-center gap-2 border border-line bg-panel px-4 py-2 text-xs text-gray-300">
                <CalendarDays className="h-3.5 w-3.5 text-accent" />
                {work.year || "—"}
              </span>
              <span className="flex items-center gap-2 border border-line bg-panel px-4 py-2 text-xs text-gray-300">
                <Tag className="h-3.5 w-3.5 text-accent" />
                {work.category}
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-line py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="clip-corner overflow-hidden border border-line">
              <img src={work.image_url || IMAGES.civil} alt={work.title} className="aspect-[16/8] w-full object-cover" />
            </div>
          </Reveal>
          <div className="mt-12 grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <p className="font-mono text-[10px] tracking-[0.35em] text-accent">OVERVIEW</p>
              <h2 className="mt-4 font-mincho text-2xl font-bold text-white">工事概要</h2>
            </div>
            <div className="lg:col-span-8">
              <Reveal delay={0.08}>
                <p className="text-sm leading-loose text-gray-300 sm:text-base">{work.description}</p>
                {work.detail && <p className="mt-6 text-sm leading-loose text-gray-400 sm:text-base">{work.detail}</p>}
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {hasBeforeAfter && (
        <section className="border-t border-line bg-coal py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading eyebrow="BEFORE / AFTER" title="施工前後ギャラリー" sub="スライダーを動かして、施工前と施工後を比較できます。" />
            <Reveal delay={0.1} className="mt-12">
              <BeforeAfter before={work.before_image_url} after={work.after_image_url} alt={work.title} />
            </Reveal>
            <p className="mt-6 font-mono text-[10px] tracking-[0.2em] text-gray-600">※ 掲載写真はイメージです。実際の施工写真は確定次第差し替えとなります。</p>
          </div>
        </section>
      )}

      <CtaSection title="この現場のように、" highlight="あなたの土地も。" text="同様の工事・ご相談はお気軽にどうぞ。現地調査・お見積りは無料です。" button="この工事について相談する" to="/contact?type=estimate" />
    </div>
  );
}
